import { createSlice } from '@reduxjs/toolkit';
import { fetchInvoices, fetchInvoiceById, createInvoice, updateInvoice, removeInvoice } from '../actions/invoiceActions';

const initialState = {
  invoices: { count: 0, items: [] },
  invoice: null,
  loading: false,
  error: null,
  status: 'idle',
  filter: {
    minAmount: null,
    maxAmount: null,
    customers: [],
  },
};

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    filterInvoices: (state, action) => {
      const { minAmount, maxAmount, customers } = action.payload;
      state.invoices.items = state.invoices.items.filter(inv => {
        const isInAmountRange = (minAmount === null || inv.amount >= minAmount) && (maxAmount === null || inv.amount <= maxAmount);
        const hasCustomer = customers.length === 0 || customers.includes(inv.customerName);
        return isInAmountRange && hasCustomer;
      });
    },
    searchInvoices: (state, action) => {
      const query = action.payload.toLowerCase();
      const originalItems = state.originalItems || state.invoices.items;
      const filteredItems = originalItems.filter(inv => {
        return (
          inv.customerName?.toLowerCase().includes(query) ||
          inv.id?.toString().toLowerCase().includes(query)
        );
      });
      state.invoices.items = filteredItems;
      state.originalItems = originalItems;
      if (query.length === 0) {
        state.invoices.items = originalItems;
        state.originalItems = null;
      }
    },
    setLoad: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        if (state.invoices.items) state.invoices.items.push(action.payload);
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        if (state.invoices.items) {
          const idx = state.invoices.items.findIndex(inv => inv.id === action.payload.id);
          if (idx !== -1) state.invoices.items[idx] = action.payload;
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(removeInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeInvoice.fulfilled, (state, action) => {
        state.loading = false;
        if (state.invoices.items) {
          state.invoices.items = state.invoices.items.filter(inv => inv.id !== action.payload.id);
        }
      })
      .addCase(removeInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  filterInvoices,
  searchInvoices,
  setLoad,
  setError,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
